import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Check, X, ArrowRight, Trophy, Tag } from 'lucide-react';
import { products } from '@/data/products';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Card, CardContent } from '@/components/ui/card';
import { useAffiliateLink } from '@/hooks/useAffiliateLink';
import { createConversionClickHandler } from '@/utils/conversionTracking';

// Star Rating Component
const StarRating = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
        <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
                <Star
                    key={i}
                    className={`h-4 w-4 ${
                        i < fullStars
                            ? 'fill-amber-400 text-amber-400'
                            : i === fullStars && hasHalfStar
                            ? 'fill-amber-400/50 text-amber-400'
                            : 'fill-muted text-muted'
                    }`}
                />
            ))}
            <span className="ml-1 text-sm font-semibold text-foreground">
                {rating.toFixed(1)}
            </span>
        </div>
    );
};

// Badge Component
const ProductBadge = ({ badge, isWinner }) => {
    if (!badge) return null;

    return (
        <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                isWinner
                    ? 'bg-amber-500 text-white'
                    : 'bg-primary/10 text-primary'
            }`}
        >
            {isWinner && <Trophy className="h-3 w-3" />}
            {!isWinner && badge === 'Preistipp' && <Tag className="h-3 w-3" />}
            {badge}
        </span>
    );
};

// Diuretic Status Component
const DiureticStatus = ({ isDiuretic }) => {
    return (
        <div className="flex items-center gap-2">
            {isDiuretic ? (
                <>
                    <Check className="h-4 w-4 text-primary" />
                    <span className="text-sm text-foreground">Als entwässernd beworben</span>
                </>
            ) : (
                <>
                    <X className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                        Keine Angabe
                    </span>
                </>
            )}
        </div>
    );
};

// Desktop Table Row Component (to use Hook)
const DesktopTableRow = ({ product }) => {
    const isWinner = product.rank === 1;
    const affiliateLink = useAffiliateLink(product.affiliateLink);
    const handleClick = createConversionClickHandler(affiliateLink);

    return (
        <tr
            className={`border-b border-border transition-colors hover:bg-muted/30 ${
                isWinner ? 'bg-winner' : ''
            }`}
        >
            <td className="py-6 px-4">
                <span
                    className={`text-2xl font-bold ${
                        isWinner
                            ? 'text-amber-600'
                            : 'text-muted-foreground'
                    }`}
                >
                    #{product.rank}
                </span>
            </td>
            <td className="py-6 px-4">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div>
                        <ProductBadge
                            badge={product.badge}
                            isWinner={isWinner}
                        />
                        <Link to={`/produkt/${product.slug}`} className="font-semibold text-foreground mt-1 hover:text-primary transition-colors block">
                            {product.name}
                        </Link>
                    </div>
                </div>
            </td>
            <td className="py-6 px-4">
                <StarRating rating={product.rating} />
            </td>
            <td className="py-6 px-4">
                <span className="text-lg font-bold text-foreground">
                    {product.price}
                </span>
            </td>
            <td className="py-6 px-4">
                <span className="text-sm text-foreground">
                    {product.taste}
                </span>
            </td>
            <td className="py-6 px-4">
                <span
                    className={`text-sm ${
                        product.caffeine === 'Koffeinfrei'
                            ? 'text-primary'
                            : 'text-foreground'
                    }`}
                >
                    {product.caffeine}
                </span>
            </td>
            <td className="py-6 px-4">
                <DiureticStatus isDiuretic={product.isDiuretic} />
            </td>
            <td className="py-6 px-4 text-center">
                <div className="flex flex-col gap-2">
                    <a
                        href={affiliateLink}
                        onClick={handleClick}
                        className="btn-cta inline-flex text-sm px-4 py-2"
                        rel="noopener noreferrer sponsored"
                    >
                        Zum Angebot
                        <ArrowRight className="h-4 w-4" />
                    </a>
                    <Link
                        to={`/produkt/${product.slug}`}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-primary text-primary hover:bg-primary/10 transition-colors font-semibold text-sm"
                    >
                        Details
                    </Link>
                </div>
            </td>
        </tr>
    );
};

// Desktop Table View
const DesktopComparisonTable = () => {
    return (
        <div className="hidden lg:block overflow-x-auto custom-scrollbar">
            <table className="w-full border-collapse">
                <thead>
                    <tr className="border-b border-border">
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Rang
                        </th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Produkt
                        </th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Bewertung
                        </th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Preis
                        </th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Geschmack
                        </th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Koffein
                        </th>
                        <th className="text-left py-4 px-4 text-sm font-semibold text-muted-foreground">
							Entwässerung
                        </th>
                        <th className="text-center py-4 px-4 text-sm font-semibold text-muted-foreground">
                            Aktion
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((product) => (
                        <DesktopTableRow key={product.id} product={product} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Mobile Card Component (to use Hook)
const MobileProductCard = ({ product }) => {
    const isWinner = product.rank === 1;
    const affiliateLink = useAffiliateLink(product.affiliateLink);
    const handleClick = createConversionClickHandler(affiliateLink);

    return (
        <Card
            className={`overflow-hidden ${
                isWinner ? 'card-winner' : ''
            }`}
        >
            <CardContent className="p-0">
                {/* Card Header */}
                <div className="p-4 pb-3">
                    {/* Top Row: Rank + Badge + Price */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            {/* Rank Number */}
                            <span
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    isWinner
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-muted text-muted-foreground'
                                }`}
                            >
                                {product.rank}
                            </span>
                            {/* Badge */}
                            {product.badge && (
                                <ProductBadge
                                    badge={product.badge}
                                    isWinner={isWinner}
                                />
                            )}
                        </div>
                        {/* Price */}
                        <span className="text-xl font-bold text-foreground">
                            {product.price}
                        </span>
                    </div>

                    {/* Row 2: Image + Product Name + Rating */}
                    <div className="flex gap-3">
                        {/* Product Image */}
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                            <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Product Name + Rating */}
                        <div className="flex-1 flex flex-col justify-center">
                            <Link to={`/produkt/${product.slug}`} className="font-semibold text-foreground text-base leading-snug hover:text-primary transition-colors">
                                {product.name}
                            </Link>
                            <div className="mt-1">
                                <StarRating rating={product.rating} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-border mx-4" />

                {/* Card Details */}
                <div className="p-4 space-y-3">
                    {/* Info Grid - 2 columns */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-muted/50 rounded-lg px-3 py-2">
                            <span className="text-muted-foreground text-xs">
                                Geschmack
                            </span>
                            <p className="text-foreground font-medium text-sm">
                                {product.taste}
                            </p>
                        </div>
                        <div className="bg-muted/50 rounded-lg px-3 py-2">
                            <span className="text-muted-foreground text-xs">
                                Koffein
                            </span>
                            <p
                                className={`font-medium text-sm ${
                                    product.caffeine === 'Koffeinfrei'
                                        ? 'text-primary'
                                        : 'text-foreground'
                                }`}
                            >
                                {product.caffeine}
                            </p>
                        </div>
                    </div>

                    {/* Diuretic Status */}
                    <div className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                        <span className="text-muted-foreground text-sm">
							Entwässerung
                        </span>
                        <DiureticStatus isDiuretic={product.isDiuretic} />
                    </div>

                    {/* Ingredients List */}
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        {product.ingredients}
                    </p>
                </div>

                {/* Card CTA */}
                <div className="p-4 pt-0 flex gap-2">
                    <Link
                        to={`/produkt/${product.slug}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-primary text-primary hover:bg-primary/10 transition-colors font-semibold text-sm"
                    >
                        Details
                    </Link>
                    <a
                        href={affiliateLink}
                        onClick={handleClick}
                        className="btn-cta flex-1 justify-center"
                        rel="noopener noreferrer sponsored"
                    >
                        Zum Angebot
                        <ArrowRight className="h-4 w-4" />
                    </a>
                </div>
            </CardContent>
        </Card>
    );
};

// Mobile Card View
const MobileComparisonCards = () => {
    return (
        <div className="lg:hidden space-y-4">
            {products.map((product) => (
                <MobileProductCard key={product.id} product={product} />
            ))}
        </div>
    );
};

export const ProductComparison = () => {
    const { ref, isVisible } = useScrollAnimation();

    return (
        <section id="vergleich" className="py-16 md:py-24">
            <div ref={ref} className="section-container">
                {/* Section Header */}
                <div
                    className={`text-center mb-10 ${
                        isVisible ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                >
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                        Detox Tee Vergleich 2026
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        Unser Vergleich der beliebtesten Detox Tees: schnell entscheiden und
                        direkt zum Anbieter wechseln.
                    </p>

                    <p className="text-xs text-muted-foreground max-w-2xl mx-auto mt-3">
                        <strong>So bewerten wir:</strong> Preis/Portion, Zutaten-Deklaration,
                        Koffeinangaben, Geschmackprofil &amp; Nutzerfeedback (Stand: Januar 2026).
                    </p>
                </div>

                {/* Comparison Views */}
                <div
                    className={`bg-card rounded-2xl shadow-card border border-border overflow-hidden ${
                        isVisible ? 'animate-fade-in-up' : 'opacity-0'
                    }`}
                    style={{ animationDelay: '0.2s' }}
                >
                    <div className="p-4 md:p-6">
                        <DesktopComparisonTable />
                        <MobileComparisonCards />
                    </div>
                </div>

                {/* Compliance Disclaimer */}
                <p
                    className={`text-xs text-muted-foreground text-center mt-6 max-w-3xl mx-auto ${
                        isVisible ? 'animate-fade-in' : 'opacity-0'
                    }`}
                    style={{ animationDelay: '0.4s' }}
                >
                    Hinweis: Der Vergleich bezieht sich auf Preis, Zutatenprofil,
                    Geschmack, Koffeinangaben und deklarierte Inhaltsstoffe. Keine
                    medizinischen Heilversprechen. Individuelle Erfahrungen können variieren.
                </p>
            </div>
        </section>
    );
};
